import { Injectable } from '@angular/core';
import { HttpRequest, HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { fromEvent, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, startWith, tap } from 'rxjs/operators';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { EXTENTIONS_MIME_TYPES, FileProgress } from '@bazis/form/models/form-element.types';
import { BazisToastService } from '@bazis/shared/services/toast.service';
import { SrvEntData, SrvResponse } from '@bazis/shared/models/srv.types';
import { convertBytesToMb } from '@bazis/utils';
import { fromFetch } from 'rxjs/fetch';
import { MAX_FILE_SIZE } from '@bazis/configuration.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';

const HEADERS = new HttpHeaders().set('Accept', 'application/vnd.api+json');

@Injectable({
    providedIn: 'root',
})
export class BazisFileService {
    constructor(
        protected http: HttpClient,
        protected toastService: BazisToastService,
        protected srv: BazisSrvService,
        protected entityService: BazisEntityService,
    ) {}

    uploadFile$(file: File, type = 'file'): Observable<FileProgress> {
        let uploadData = new FormData();
        uploadData.append('file', file, file.name);
        uploadData.append('name', file.name);
        let urlMap = {
            file: 'file_upload',
            link: 'file_upload',
            photo: 'file_upload',
        };
        return this.http
            .request(
                new HttpRequest(
                    'POST',
                    `${this.srv.apiUrl}/uploadable/${urlMap[type]}/`,
                    uploadData,
                    {
                        headers: HEADERS,
                        reportProgress: true,
                    },
                ),
            )
            .pipe(
                map(
                    (event) =>
                        ({
                            file: file,
                            state: this.getProgress(event, file),
                        } as FileProgress),
                ),
                catchError((error) =>
                    of({
                        file: file,
                        state: this.getErrorProgress(file, error),
                    } as FileProgress),
                ),
                startWith({
                    file: file,
                    state: {
                        progress: 0,
                        status: 'init',
                    },
                } as FileProgress),
            );
    }

    simpleFileUpload$(
        file: File,
        includeName = true,
        fileFieldName = 'file',
        isEntity = true,
        url = '/uploadable/file_upload/',
    ): Observable<any> {
        let uploadData = new FormData();
        uploadData.append(fileFieldName, file, file.name);
        if (includeName) uploadData.append('name', file.name);
        return this.http
            .post(`${this.srv.apiUrl}${url}`, uploadData, {
                headers: HEADERS,
            })
            .pipe(
                map((response: SrvResponse) =>
                    isEntity ? this.srv.makeupEntity(response.data as SrvEntData) : response,
                ),
                tap((data) => {
                    if (isEntity) this.entityService.setEntity(data);
                }),
            );
    }

    protected getProgress(event: HttpEvent<any>, file: File) {
        switch (event.type) {
            case HttpEventType.Sent:
                return {
                    progress: 0,
                    status: 'start',
                    message: `Файл "${file.name}" размером ${file.size} загружается`,
                };

            case HttpEventType.DownloadProgress:
            case HttpEventType.UploadProgress:
                const p = Math.round((100 * event.loaded) / event.total);
                return {
                    progress: p,
                    status: 'progress',
                };

            case HttpEventType.Response:
                let ent = this.srv.makeupEntity(event.body.data);
                this.entityService.setEntity(ent);
                return {
                    progress: 100,
                    status: 'success',
                    id: ent.id,
                    ent,
                };
            default:
                return {
                    progress: 0,
                    status: 'failed',
                };
        }
    }

    protected getErrorProgress(file: File, errorResponse: HttpErrorResponse | any): any {
        let errorMessage = `Статус: ${errorResponse.status} - ${errorResponse.statusText || '?'}`;
        if (errorResponse.error && errorResponse.error.errors)
            errorMessage = errorResponse.error.errors.reduce(
                (msg, err) => `${msg ? msg + '; ' : ''}${err.code} - ${err.detail}`,
                null,
            );

        this.toastService.create({
            titleKey: 'toast.fileUploadingError.title',
            message: `"${file.name}" - ${errorMessage}`,
            position: 'top',
        });

        console.error('[!][FILE]', errorResponse, errorMessage);
        return { progress: 10, status: 'failed', message: errorMessage };
    }

    checkRealMimeType(file: File, header: string, extensions) {
        const mimeCodes = extensions.reduce(
            (acc, extension) =>
                EXTENTIONS_MIME_TYPES[extension]
                    ? acc.concat(EXTENTIONS_MIME_TYPES[extension])
                    : acc,
            [],
        );
        header = header.toUpperCase();
        return (
            mimeCodes.length === 0 ||
            mimeCodes.findIndex((mimeType) => header.indexOf(mimeType) === 0) > -1
        );
    }

    readFile$(file: File, extensions) {
        let blob = file.slice(0, 4);
        let fileReader = new FileReader();

        const observable$ = fromEvent(fileReader, 'loadend').pipe(
            mergeMap((e: any) => {
                let arr = new Uint8Array(e.target.result).subarray(0, 4);
                let header = '';
                for (let i = 0; i < arr.length; i++) {
                    header += arr[i].toString(16);
                }
                if (!this.checkRealMimeType(file, header, extensions)) {
                    return throwError(() => ({ error: 'mimeTypeError', header }));
                }
                return of(file);
            }),

            catchError((e) => {
                if (e?.error === 'mimeTypeError') {
                    console.error(
                        'file code: ',
                        e.header,
                        ', explicit file name: ',
                        file.name,
                        ', explicit file type: ',
                        file.type,
                    );
                    this.toastService.create({
                        type: 'error',
                        titleKey: 'form.toast.fileErrorMime.title',
                        messageKey: 'form.toast.fileErrorMime.message',
                    });
                }

                return throwError(e);
            }),
        );

        fileReader.readAsArrayBuffer(blob);

        return observable$;
    }

    preUploadFile(
        file: File,
        element: HTMLInputElement,
        extensions,
        maxFileSize = MAX_FILE_SIZE,
    ): { error: string; extension: string; size: number } {
        const extensionArr = file.name.toLowerCase().split('.');
        const extension = extensionArr[extensionArr.length - 1];

        if (extensions.indexOf(extension) === -1) {
            this.toastService.create({
                type: 'error',
                titleKey: 'form.toast.fileErrorExtention.title',
                messageKey: 'form.toast.fileErrorExtention.message',
                messageParams: {
                    allowedExtentions: extensions.join(', '),
                    currentExtention: extension,
                },
            });
            if (element) element.value = null;
            return { error: 'fileErrorExtention', extension, size: file.size };
        }
        if (file.size > maxFileSize) {
            this.toastService.create({
                type: 'error',
                titleKey: 'form.toast.fileErrorSize.title',
                messageKey: 'form.toast.fileErrorSize.message',
                messageParams: {
                    allowedSize: convertBytesToMb(maxFileSize).toFixed(0),
                    currentSize: convertBytesToMb(file.size).toFixed(2),
                },
            });
            if (element) element.value = null;
            return { error: 'fileErrorSize', extension, size: file.size };
        }

        return { error: null, extension, size: file.size };
    }

    fullUploadProcess$(file: File, extensions, maxFileSize = MAX_FILE_SIZE) {
        const result = this.preUploadFile(file, null, extensions, maxFileSize);
        if (result.error) return;

        return this.readFile$(file, extensions).pipe(mergeMap((v) => this.uploadFile$(file)));
    }

    base64ToFile$(dataUrl, fileName): Observable<File> {
        return fromFetch(dataUrl).pipe(
            mergeMap((r) => r.blob()),
            map((blob) => new File([blob], fileName, { type: blob.type })),
        );
    }
}
